import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/admin/roles"), api.get("/admin/permissions")]);
      setRoles(r.data); setPerms(p.data);
      const d = {}; r.data.forEach((x) => { d[x.name] = new Set(x.permissions || []); }); setDraft(d);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { queueMicrotask(load); }, [load]);

  const cats = useMemo(() => [...new Set(perms.map((p) => p.category))], [perms]);
  const permsByCategory = useMemo(() => {
    const m = {};
    perms.forEach((p) => { (m[p.category] = m[p.category] || []).push(p); });
    return m;
  }, [perms]);
  const toggle = (role, key) => {
    setDraft((d) => { const s = new Set(d[role]); s.has(key) ? s.delete(key) : s.add(key); return { ...d, [role]: s }; });
  };
  const save = async (role) => {
    try { await api.patch(`/admin/roles/${role}`, { permissions: [...draft[role]] }); toast.success(`Saved ${role}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-10 text-[#50493b] flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] subtitle tracking-[0.3em] uppercase text-[#39678f] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Administration</div>
        <Link to="/admin/users" className="text-xs subtitle uppercase tracking-wider text-[#39678f] hover:underline">← Users</Link>
      </div>
      <h1 className="font-display text-5xl tracking-tight text-[#33424f] leading-none mb-6">Roles &amp; Permissions</h1>

      <div className="space-y-5" data-testid="roles-list">
        {roles.map((role) => (
          <div key={role.name} data-testid={`role-${role.name}`} className="border border-[#33424f]/20 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div><div className="subtitle uppercase tracking-[0.15em] text-[#33424f]">{role.name}</div><div className="text-xs text-[#50493b]">{role.description} · {role.user_count} user(s)</div></div>
              {role.name !== "super-admin" && (
                <Button data-testid={`save-role-${role.name}`} onClick={() => save(role.name)} className="h-8 bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-[10px]">Save</Button>)}
            </div>
            {role.name === "super-admin" ? <div className="text-xs text-[#7a6f57] italic">Super Admin always has every permission.</div> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                {cats.map((cat) => (
                  <div key={cat} className="py-1">
                    <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#39678f] mb-1">{cat}</div>
                    {(permsByCategory[cat] || []).map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-xs text-[#33424f] py-0.5 cursor-pointer">
                        <input type="checkbox" data-testid={`perm-${role.name}-${p.key}`} checked={draft[role.name]?.has(p.key) || false} onChange={() => toggle(role.name, p.key)} />
                        {p.name} <span className="text-[10px] text-[#9a917f]">{p.key}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
